import { Directive, ElementRef, OnDestroy, OnInit, Renderer2, input, inject } from '@angular/core';

import { AnalyticsDirectiveData } from './analytics.model';
import { ApAnalyticsService } from './analytics.service';

/**
 * @ngdoc directive
 * @name ApAnalyticsDirective
 * @module ApAnalyticsModule
 * @description Directive that can be used to push data to fc-datalayer when an event on the element is triggered.
 * Usage of this directive is discouraged, instead rather make use of {@link ApAnalyticsService}  instead, since it has typing.
 *
 * @howToUse
 * apAnalytics accepts an object with 2 properties: `on` and `data`.
 *
 * @param {AnalyticsDirectiveData} [apAnalytics] The event when the data needs to be pushed and the data that needs to be pushed, {@link AnalyticsData}.
 * ```
 * <button [apAnalytics]='{
 *        "on":"click",
 *        "data": {
 *            "event":"chat",
 *            "title": "It works from HTML!",
 *            "analyticsEvents": [
 *                form.get("inputPublic").value
 *            ]}
 *        }'>Click
 * </button>
 * ```
 */
@Directive({ selector: '[apAnalytics]' })
export class ApAnalyticsDirective implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly analyticsService = inject(ApAnalyticsService);

    readonly data = input<AnalyticsDirectiveData>(undefined, { alias: 'apAnalytics' });
    private dispose: () => void;

    ngOnInit(): void {
        this.dispose = this.renderer.listen(this.elementRef.nativeElement, this.data().on, this.pushData());
    }

    ngOnDestroy(): void {
        this.dispose();
    }

    private pushData(): (_: any) => void {
        return () => this.analyticsService.push(this.data().data);
    }
}
